import { appendSearchParams } from '@alexis/helpers/searchParam';
import { BehaviorSubject, timer } from 'rxjs';
import type { Observable } from 'rxjs';
import { fromFetch } from 'rxjs/fetch';
import { concatMap, finalize, share, takeWhile, tap } from 'rxjs/operators';

import { getGenzoIdentifierId } from '@helpers/genzoIdentifierId';

import { HotelRateResult } from '@wegoTypes/hotels/metasearch/hotelRateResult';

export function hotelRateObservable(
  url: string,
  hotelId: number,
  searchId: string,
  locale: string,
  currencyCode: string,
  amountType: string,
  wegoAnalyticsClientId: string | undefined,
  wegoAnalyticsClientSessionId: string | undefined,
  similarHotelLimit: number,
  isGoogleHotel?: boolean,
): Observable<HotelRateResult> {
  const MAX_POLLING_COUNT = isGoogleHotel ? 12 : 6;
  const POLLING_DELAY = 3000;
  const pollingSubject = new BehaviorSubject<number>(1);

  function buildUrl(pollingCount: number): string {
    return appendSearchParams(`${url}/${hotelId}/rates`, {
      searchId,
      locale,
      currencyCode,
      amountType,
      clientId: wegoAnalyticsClientId,
      output: 'ALL_RATES',
      similarHotelLimit,
      ...(pollingCount === MAX_POLLING_COUNT && { isLastPolling: true }),
    });
  }

  function fetchHotelRate(pollingCount: number): Observable<HotelRateResult> {
    return fromFetch(buildUrl(pollingCount), {
      method: 'GET',
      credentials: 'include',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        ...(!!getGenzoIdentifierId() && {
          'X-Wego-Genzo-Identifier-Id': getGenzoIdentifierId(),
        }),
        'X-Wego-Client-Id': wegoAnalyticsClientId!,
        'X-Wego-Session-Id': wegoAnalyticsClientSessionId!,
      },
    }).pipe(
      concatMap(async (response: Response) => {
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        return (await response.json()) as HotelRateResult;
      }),
    );
  }

  return pollingSubject.pipe(
    takeWhile((pollingCount) => pollingCount <= MAX_POLLING_COUNT),
    concatMap((pollingCount: number) => {
      return fetchHotelRate(pollingCount).pipe(
        tap(() => {
          if (pollingCount < MAX_POLLING_COUNT) {
            timer(POLLING_DELAY).subscribe(() => {
              pollingSubject.next(pollingCount + 1);
            });
          } else {
            pollingSubject.complete();
          }
        }),
      );
    }),
    finalize(() => {
      pollingSubject.complete();
    }),
    share({
      resetOnError: false,
      resetOnComplete: false,
      resetOnRefCountZero: false,
    }),
  );
}
